<template>
  <transition name="modal-fade">
    <div v-if="isVisible" class="modal-overlay" @click.self="closeModal">
      <div class="rankings-modal modal-container">
        <div class="modal-header">
          <h2><i class="fas fa-trophy"></i> RANKINGS</h2>
          <button class="close-button" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="ranking-categories">
          <div class="category-section">
            <h3>JOGADORES</h3>
            <div class="category-buttons">
              <button 
                v-for="category in playerCategories" 
                :key="category.id" 
                @click="setActiveRanking(category.id)"
                :class="{ active: activeRanking === category.id }"
                class="category-button"
              >
                <i :class="category.icon"></i> {{ category.name }}
              </button>
            </div>
          </div>
          
          <div class="category-section">
            <h3>LUTADORES</h3>
            <div class="category-buttons">
              <button 
                v-for="category in fighterCategories" 
                :key="category.id" 
                @click="setActiveRanking(category.id)"
                :class="{ active: activeRanking === category.id }"
                class="category-button"
              >
                <i :class="category.icon"></i> {{ category.name }}
              </button>
            </div>
          </div>
        </div>
        
        <div class="ranking-content">
          <template v-if="loading">
            <div class="loading-container">
              <div class="cyber-spinner"></div>
              <p>Carregando ranking...</p>
            </div>
          </template>
          
          <template v-else-if="activeRankingData.rankings && activeRankingData.rankings.length > 0">
            <h3 class="ranking-title">
              {{ getRankingTitle() }}
              <span class="refresh-button" @click="refreshRanking" title="Atualizar Ranking">
                <i class="fas fa-sync-alt"></i>
              </span>
            </h3>
            
            <div class="ranking-table-container">
              <table class="ranking-table">
                <thead>
                  <tr>
                    <th class="position-col">POS</th>
                    <th class="name-col">NOME</th>
                    <th class="value-col">{{ getValueColumnName() }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="(item, index) in activeRankingData.rankings" 
                    :key="index"
                    :class="{ 'is-current-player': isCurrentEntity(item) }"
                  >
                    <td class="position-col">{{ index + 1 }}</td>
                    <td class="name-col">
                      <div class="player-info">
                        <span v-if="isPlayerRanking">
                          <router-link :to="`/player/${item.companyName}`" class="player-link">
                            {{ item.companyName || 'Jogador' }}
                          </router-link>
                        </span>
                        <span v-else>
                          <span v-if="item.rank" class="fighter-rank">[{{ item.rank }}]</span>
                          <strong :class="{'my-fighter': isCurrentEntity(item)}">{{ item.name || 'Lutador' }}</strong>
                          <small v-if="item.player?.companyName">
                            (<router-link :to="`/player/${item.player.companyName}`" class="player-link">
                              {{ item.player.companyName }}
                            </router-link>)
                          </small>
                        </span>
                      </div>
                    </td>
                    <td class="value-col">{{ formatRankingValue(item) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Player position if not in top rankings - ONLY for player rankings, not fighters -->
            <div 
              v-if="isPlayerRanking && activeRankingData.position && !activeRankingData.position.inTopList" 
              class="player-position"
            >
              <div class="position-divider"></div>
              <div class="position-info">
                <i class="fas fa-user"></i>
                <span>Sua posição: <strong>#{{ activeRankingData.position.position }}</strong></span>
              </div>
            </div>
          </template>
          
          <template v-else>
            <div class="empty-ranking">
              <i class="fas fa-exclamation-circle"></i>
              <p>Nenhum dado de ranking disponível.</p>
              <button @click="refreshRanking" class="refresh-button-empty">
                <i class="fas fa-sync-alt"></i> Tentar Novamente
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'

export default {
  name: 'ModalRankings',
  data() {
    return {
      playerCategories: [
        { id: 'coins', name: 'Moedas', icon: 'fas fa-coins' },
        { id: 'paralleliums', name: 'Paralleliums', icon: 'fas fa-gem' },
        { id: 'businesses', name: 'Negócios', icon: 'fas fa-building' },
        { id: 'level', name: 'Nível', icon: 'fas fa-star' }
      ],
      fighterCategories: [
        { id: 'victories', name: 'Vitórias', icon: 'fas fa-fist-raised' },
        { id: 'pvpVictories', name: 'Vitórias PvP', icon: 'fas fa-users' },
        { id: 'pvpDefeat', name: 'Sobrevivência PvP', icon: 'fas fa-shield-alt' }
      ]
    }
  },
  computed: {
    ...mapGetters('rankings', [
      'isRankingsModalVisible', 
      'activeRanking',
      'activeRankingData'
    ]),
    ...mapState('rankings', ['loading']),
    ...mapState('player', ['playerData']),
    isVisible() {
      return this.isRankingsModalVisible
    },
    isPlayerRanking() {
      return ['coins', 'paralleliums', 'businesses', 'level'].includes(this.activeRanking)
    },
    hasFighter() {
      return this.playerData && this.playerData.fighter;
    }
  },
  watch: {
    // Replace the existing watchers with this single watcher
    isVisible(newValue) {
      // Only load ranking data when the modal becomes visible
      if (newValue && this.playerData) {
        console.log('Modal opened, loading current ranking:', this.activeRanking);
        this.loadActiveRanking();
      }
    }
  },
  methods: {
    ...mapActions('rankings', [
      'toggleRankingsModal', 
      'setActiveRanking',
      'fetchPlayerRankingByType',
      'fetchFighterRankingByType'
    ]),
    ...mapActions('SoundController', ['playSound']),
    closeModal() {
      this.toggleRankingsModal(false)
      if (!this.$store.state.SoundController.isSoundMuted) {
        this.playSound({ category: 'ui', type: 'click' })
      }
    },
    // Replace loadInitialRanking with this method
    async loadActiveRanking() {
      try {
        // Only load if the modal is visible
        if (!this.isVisible) return;
        
        if (this.isPlayerRanking) {
          await this.fetchPlayerRankingByType(this.activeRanking);
        } else {
          // Always fetch fighter rankings, even if player has no fighter
          await this.fetchFighterRankingByType(this.activeRanking);
        }
      } catch (error) {
        console.error('Error loading ranking:', error);
      }
    },
    // Modify refreshRanking to reuse loadActiveRanking
    async refreshRanking() {
      try {
        await this.loadActiveRanking();
        
        if (!this.$store.state.SoundController.isSoundMuted) {
          this.playSound({ category: 'ui', type: 'success' });
        }
      } catch (error) {
        console.error('Error refreshing ranking:', error);
        if (!this.$store.state.SoundController.isSoundMuted) {
          this.playSound({ category: 'ui', type: 'error' });
        }
      }
    },
    // Modify setActiveRanking to avoid loading fighter rankings when not applicable
    async setActiveRanking(rankingType) {
      // Set the active ranking type without checking for fighter
      this.$store.commit('rankings/SET_ACTIVE_RANKING', rankingType);
      
      // Load the new ranking data
      if (this.isVisible) {
        await this.loadActiveRanking();
      }
      
      if (!this.$store.state.SoundController.isSoundMuted) {
        this.playSound({ category: 'ui', type: 'click' });
      }
    },
    getRankingTitle() {
      const categoryMap = {
        coins: 'JOGADORES COM MAIS MOEDAS',
        paralleliums: 'JOGADORES COM MAIS PARALLELIUMS',
        businesses: 'JOGADORES COM MAIS NEGÓCIOS',
        level: 'JOGADORES DE MAIOR NÍVEL',
        victories: 'TOP 50 LUTADORES COM MAIS VITÓRIAS',
        pvpVictories: 'TOP 50 LUTADORES COM MAIS VITÓRIAS PVP',
        pvpDefeat: 'TOP 50 LUTADORES COM MAIS DERROTAS PVP'
      }
      
      return categoryMap[this.activeRanking] || 'RANKING'
    },
    getValueColumnName() {
      const columnMap = {
        coins: 'MOEDAS',
        paralleliums: 'PARALLELIUMS',
        businesses: 'NEGÓCIOS',
        level: 'NÍVEL',
        victories: 'VITÓRIAS',
        pvpVictories: 'VITÓRIAS PVP',
        pvpDefeat: 'DERROTAS PVP'
      }
      
      return columnMap[this.activeRanking] || 'VALOR'
    },
    formatRankingValue(item) {
      if (!item) return '0'
      
      switch (this.activeRanking) {
        case 'coins':
          return this.formatNumber(item.coins || 0)
        case 'paralleliums':
          return this.formatNumber(item.paralleliums || 0)
        case 'businesses':
          return item.businesses?.length || 0
        case 'level':
          return `${item.level || 0} (${this.formatNumber(item.experience || 0)} XP)`
        case 'victories':
          return this.formatNumber(item.victories || 0)
        case 'pvpVictories':
          return this.formatNumber(item.pvpVictories || 0)
        case 'pvpDefeat':
          return this.formatNumber(item.pvpDefeat || 0)
        default:
          return '0'
      }
    },
    formatNumber(num) {
      return num.toLocaleString()
    },
    isCurrentEntity(item) {
      if (!this.playerData) return false
      
      if (this.isPlayerRanking) {
        return item.id === this.playerData.id
      } else {
        return this.playerData.fighter && item.id === this.playerData.fighter.id
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700&family=Rajdhani:wght@400;500;700&display=swap');

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(3px);
}

.rankings-modal {
  background: linear-gradient(135deg, #1e1e1e 0%, #0a0a0a 100%);
  border-radius: 3px;
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 0 30px rgba(196, 30, 58, 0.4), 0 0 10px rgba(0, 0, 0, 0.8);
  color: #e0e0e0;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(196, 30, 58, 0.5);
  font-family: 'Rajdhani', sans-serif;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #c41e3a, transparent);
  }
  
  &::-webkit-scrollbar {
    width: 8px;
    background: rgba(0, 0, 0, 0.3);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(196, 30, 58, 0.5);
    border-radius: 4px;
    
    &:hover {
      background: rgba(196, 30, 58, 0.8);
    }
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.5rem;
  border-bottom: 2px solid rgba(196, 30, 58, 0.3);
  background: rgba(0, 0, 0, 0.3);
  
  h2 {
    margin: 0;
    font-size: 1.6rem;
    font-weight: 700;
    color: #c41e3a;
    text-shadow: 0 0 10px rgba(196, 30, 58, 0.5);
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
    
    i {
      margin-right: 10px;
    }
  }
  
  .close-button {
    background: rgba(196, 30, 58, 0.2);
    border: 1px solid rgba(196, 30, 58, 0.3);
    color: #c41e3a;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 3px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    
    &:hover {
      background: rgba(196, 30, 58, 0.4);
      color: #fff;
      box-shadow: 0 0 10px rgba(196, 30, 58, 0.5);
    }
  }
}

.ranking-categories {
  padding: 1.5rem;
  border-bottom: 1px solid rgba(196, 30, 58, 0.3);
  background: rgba(0, 0, 0, 0.2);
  
  .category-section {
    margin-bottom: 1.2rem;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    h3 {
      margin: 0 0 0.8rem 0;
      font-size: 1.2rem;
      color: #c41e3a;
      font-weight: 700;
      font-family: 'Orbitron', sans-serif;
      letter-spacing: 1px;
    }
  }
  
  .category-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    
    .category-button {
      background: rgba(0, 0, 0, 0.4);
      border: 1px solid rgba(196, 30, 58, 0.3);
      border-radius: 3px;
      padding: 0.7rem 1rem;
      color: #ccc;
      cursor: pointer;
      transition: all 0.2s;
      font-family: 'Rajdhani', sans-serif;
      font-weight: 500;
      font-size: 0.95rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      
      i {
        font-size: 1rem;
        color: rgba(196, 30, 58, 0.7);
      }
      
      &:hover {
        background: rgba(196, 30, 58, 0.2);
        transform: translateY(-2px);
        color: #fff;
      }
      
      &.active {
        background: rgba(196, 30, 58, 0.3);
        border-color: #c41e3a;
        color: #fff;
        font-weight: 700;
        
        i {
          color: #c41e3a;
        }
        
        &::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
          height: 2px;
          background: #c41e3a;
          box-shadow: 0 0 10px rgba(196, 30, 58, 0.8);
        }
      }
      
      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        background: rgba(0, 0, 0, 0.3);
        border-color: rgba(100, 100, 100, 0.3);
        
        i {
          color: rgba(150, 150, 150, 0.5);
        }
        
        &:hover {
          transform: none;
          background: rgba(0, 0, 0, 0.3);
          color: #999;
        }
      }
    }
  }
}

.ranking-content {
  padding: 1.5rem;
  flex: 1;
  
  .ranking-title {
    margin: 0 0 1.5rem 0;
    font-size: 1.3rem;
    color: #fff;
    font-weight: 700;
    text-align: center;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
    position: relative;
    padding-bottom: 0.8rem;
    border-bottom: 1px solid rgba(196, 30, 58, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    
    .refresh-button {
      margin-left: 1rem;
      width: 32px;
      height: 32px;
      background: rgba(196, 30, 58, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid rgba(196, 30, 58, 0.4);
      cursor: pointer;
      transition: all 0.3s;
      
      &:hover {
        transform: rotate(180deg);
        background: rgba(196, 30, 58, 0.4);
      }
    }
  }
}

.ranking-table-container {
  max-height: 400px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
    background: rgba(0, 0, 0, 0.3);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(196, 30, 58, 0.5);
    border-radius: 3px;
  }
}

.ranking-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  
  th, td {
    padding: 0.8rem 1rem;
    border-bottom: 1px solid rgba(196, 30, 58, 0.2);
  }
  
  th {
    background: rgba(0, 0, 0, 0.3);
    color: #c41e3a;
    font-weight: 700;
    font-family: 'Orbitron', sans-serif;
    font-size: 0.9rem;
    letter-spacing: 1px;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  tr {
    background: rgba(0, 0, 0, 0.1);
    transition: all 0.2s;
    
    &:nth-child(even) {
      background: rgba(0, 0, 0, 0.2);
    }
    
    &:hover {
      background: rgba(196, 30, 58, 0.1);
    }
    
    &.is-current-player {
      background: rgba(196, 30, 58, 0.2);
      position: relative;
      
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background: #c41e3a;
        box-shadow: 0 0 8px rgba(196, 30, 58, 0.8);
      }
      
      td {
        color: #fff;
        font-weight: 700;
      }
    }
  }
  
  .position-col {
    width: 60px;
    text-align: center;
    font-weight: 700;
    font-family: 'Orbitron', sans-serif;
    
    // Top 3 positions
    tr:nth-child(1) & {
      color: gold;
      text-shadow: 0 0 5px rgba(255, 215, 0, 0.5);
    }
    
    tr:nth-child(2) & {
      color: silver;
      text-shadow: 0 0 5px rgba(192, 192, 192, 0.5);
    }
    
    tr:nth-child(3) & {
      color: #cd7f32; // bronze
      text-shadow: 0 0 5px rgba(205, 127, 50, 0.5);
    }
  }
  
  .name-col {
    width: 60%;
    
    .player-info {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }
  
  .value-col {
    text-align: right;
    font-weight: 600;
    font-family: 'Rajdhani', sans-serif;
  }
}

.player-position {
  margin-top: 1.5rem;
  
  .position-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(196, 30, 58, 0.5), transparent);
    margin-bottom: 1rem;
  }
  
  .position-info {
    background: rgba(196, 30, 58, 0.15);
    border-radius: 4px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    border: 1px solid rgba(196, 30, 58, 0.3);
    font-weight: 500;
    
    i {
      color: #c41e3a;
      font-size: 1.2rem;
    }
    
    strong {
      color: #c41e3a;
      font-weight: 700;
      font-size: 1.1rem;
    }
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 1.5rem;
  
  .cyber-spinner {
    width: 50px;
    height: 50px;
    border: 3px solid rgba(196, 30, 58, 0.3);
    border-top-color: #c41e3a;
    border-radius: 50%;
    animation: cyberspin 1s infinite linear;
    position: relative;
    
    &::before, &::after {
      content: '';
      position: absolute;
      top: -3px;
      left: -3px;
      right: -3px;
      bottom: -3px;
      border-radius: 50%;
      border: 3px solid transparent;
      animation: cyber-glow 2s infinite;
    }
    
    &::after {
      animation-delay: 1s;
    }
  }
  
  p {
    color: #ccc;
    font-size: 1.1rem;
  }
}

.empty-ranking {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 1.5rem;
  text-align: center;
  
  i {
    font-size: 3rem;
    color: rgba(196, 30, 58, 0.6);
  }
  
  p {
    color: #ccc;
    font-size: 1.1rem;
  }
  
  .refresh-button-empty {
    background: rgba(196, 30, 58, 0.2);
    border: 1px solid rgba(196, 30, 58, 0.4);
    padding: 0.8rem 1.5rem;
    color: #fff;
    font-weight: 600;
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &:hover {
      background: rgba(196, 30, 58, 0.4);
      transform: translateY(-2px);
    }
  }
}

@keyframes cyberspin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes cyber-glow {
  0% {
    border-color: rgba(196, 30, 58, 0);
    box-shadow: 0 0 0 rgba(196, 30, 58, 0);
  }
  50% {
    border-color: rgba(196, 30, 58, 0.5);
    box-shadow: 0 0 15px rgba(196, 30, 58, 0.3);
  }
  100% {
    border-color: rgba(196, 30, 58, 0);
    box-shadow: 0 0 0 rgba(196, 30, 58, 0);
  }
}

.modal-fade-enter-active, .modal-fade-leave-active {
  transition: all 0.3s;
}

.modal-fade-enter, .modal-fade-leave-to {
  opacity: 0;
  
  .rankings-modal {
    transform: scale(0.95);
  }
}

.fighter-rank {
  color: #ffd700;
  margin-right: 8px;
  font-weight: bold;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-shadow: 0 0 5px rgba(255, 215, 0, 0.5);
}

.my-fighter {
  color: #ff0054;
  text-shadow: 0 0 8px rgba(255, 0, 84, 0.5);
  position: relative;
  
  &::after {
    content: ' ★';
    color: #ff0054;
    margin-left: 3px;
  }
}

.player-link {
  color: inherit;
  text-decoration: none;
  transition: all 0.2s;
  padding: 2px 4px;
  border-radius: 4px;
  
  &:hover {
    color: #fff;
    background: rgba(196, 30, 58, 0.3);
    text-decoration: underline;
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
  }
}

.is-current-player .player-link {
  color: #fff;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}
</style>
